import * as React from 'react';

import { Link } from 'react-scroll';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING, SPEED } from '../../../../packages/constants';
import { AdditionalPage, ChevronRight } from '../../../../packages/icons';
import { LoadingSpinner } from '../../../loading-spinner';

interface ThumbnailProps {
    href: string;
    title: string;
    additionalPageNumber?: string;
    activeClass?: string;
    className?: string;
    containerId?: string;
    duration?: number;
    isLoading?: boolean;
    isAdditionalPage?: boolean;
    imageSrc?: string;
    offset?: number;
    onSetActive?: (e: any) => void;
    onSetInactive?: (e: any) => void;
    ref?: any;
    showThumbnail?: boolean;
    smooth?: boolean;
    spy?: boolean;
    to?: any;
}

interface StyledThumbnailProps {
    isAdditionalPage?: boolean;
    showThumbnail?: boolean;
    onSetInactive?: any;
}

interface ImageThumbnailProps {
    isAdditionalPage?: boolean;
}

const StyledLoadingSpinner = styled(LoadingSpinner)``;

const DROP_SHADOW_VALUE = `
    0 1px 5px rgba(4, 37, 91, 0.25)
`;
const PAGINATION_THUMBNAIL_WIDTH = `220px`;
const PAGINATION_THUMBNAIL_HEIGHT = `170px`;
const PAGINATION_THUMBNAIL_STYLED = `
    box-sizing: border-box;
    box-shadow: ${DROP_SHADOW_VALUE};
    border-style: solid;
    border-width: 2px;
    border-color: transparent;
    border-radius: ${RADIUS.DEFAULT};
    height: ${PAGINATION_THUMBNAIL_HEIGHT};
    margin-bottom: ${SPACING.SMALL};
    padding: ${SPACING.X_SMALL} ${SPACING.X_SMALL} ${SPACING.XX_SMALL};
    transition: border-color ${SPEED.TRANSITION};
    width: ${PAGINATION_THUMBNAIL_WIDTH};
`;

const ImagePlaceholder = styled.div`
    background-color: ${COLOR.PRIMARY.WHITE};
    display: flex;
    justify-content: center;
    ${PAGINATION_THUMBNAIL_STYLED}
`;

const ImageThumbnail = styled.img<ImageThumbnailProps>`
    ${PAGINATION_THUMBNAIL_STYLED}
`;

const StyledImage = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;

const StyledAdditionalImage = styled(AdditionalPage)`
    display: block;
    width: 100%;
    height: auto;
`;

const StyledThumbnailLabel = styled.div<StyledThumbnailProps>`
    align-self: center;
    background-color: transparent;
    border-radius: ${RADIUS.DEFAULT};
    color: ${COLOR.MAIN.DARK_GREY};
    display: inline-block;
    font-size: ${FONT_SIZE.SMALL};
    padding: ${SPACING.SMALL};
    transition: color ${SPEED.TRANSITION}, background-color ${SPEED.TRANSITION};
`;

const StyledTextLabel = styled.div<StyledThumbnailProps>`
    align-items: center;
    background-color: transparent;
    color: ${COLOR.BRAND.REPORTING};
    display: flex;
    font-weight: ${'400'};
    font-size: ${FONT_SIZE.SMALL};
    justify-content: space-between;
    padding: 5px;
    text-align: left;
    transition: color ${SPEED.TRANSITION};
`;

const StyledChevron = styled(ChevronRight)`
    fill: #b2bccd;
    height: 12px;
    width: 12px;
    opacity: 0;
`;

const StyledThumbnailWrapper = styled(Link)<StyledThumbnailProps>`
    border: 0;
    cursor: pointer;
    display: block;
    margin: 0;
    padding: 0;
    text-align: center;
    text-decoration: none;

    &.active {
        ${ImagePlaceholder},
        ${ImageThumbnail} {
            border-color: ${COLOR.BRAND.REPORTING};
        }

        ${StyledThumbnailLabel} {
            background-color: ${COLOR.BRAND.REPORTING};
            color: ${COLOR.PRIMARY.WHITE};
        }
        ${StyledTextLabel} {
            background-color: ${COLOR.PRIMARY.WHITE};
        }
        ${StyledChevron} {
            opacity: 1;
        }
    }

    &:hover {
        ${StyledThumbnailLabel} {
            background-color: ${COLOR.BRAND.REPORTING};
            color: ${COLOR.PRIMARY.WHITE};
        }
        ${StyledTextLabel} {
            background-color: ${COLOR.PRIMARY.WHITE};
            color: ${COLOR.BRAND.REPORTING};
        }
    }
`;

const Thumbnail = React.forwardRef(
    (
        {
            activeClass,
            additionalPageNumber,
            className,
            containerId,
            href,
            imageSrc,
            isLoading,
            isAdditionalPage,
            offset,
            onSetActive,
            onSetInactive,
            showThumbnail,
            title,
            to,
            ...props
        }: ThumbnailProps,
        ref,
    ) => (
        <StyledThumbnailWrapper
            activeClass={activeClass}
            className={className}
            containerId={containerId}
            duration={500}
            href={href}
            offset={offset}
            onSetActive={onSetActive}
            onSetInactive={onSetInactive}
            smooth
            spy={true}
            to={to}
            ref={ref}
            {...props}
        >
            {showThumbnail ? (
                isLoading ? (
                    <>
                        <ImagePlaceholder>
                            <StyledLoadingSpinner color={'black'} justify={'space-between'} />
                        </ImagePlaceholder>
                        <StyledThumbnailLabel>
                            {title}
                            {additionalPageNumber && ` (page ${Number(additionalPageNumber) + 1})`}
                        </StyledThumbnailLabel>
                    </>
                ) : (
                    <>
                        <ImagePlaceholder>
                            {isAdditionalPage ? <StyledAdditionalImage /> : <StyledImage src={imageSrc} alt={title} />}
                        </ImagePlaceholder>
                        <StyledThumbnailLabel>
                            {title}
                            {additionalPageNumber && ` (page ${Number(additionalPageNumber) + 1})`}
                        </StyledThumbnailLabel>
                    </>
                )
            ) : (
                <StyledTextLabel>
                    {title}
                    {additionalPageNumber && ` (page ${Number(additionalPageNumber) + 1})`}
                    <StyledChevron />
                </StyledTextLabel>
            )}
        </StyledThumbnailWrapper>
    ),
);
Thumbnail.displayName = 'Thumbnail';

export default Thumbnail;
export { StyledLoadingSpinner, DROP_SHADOW_VALUE, PAGINATION_THUMBNAIL_WIDTH };
